import React, { useState, useEffect, useRef } from 'react';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import DOMPurify from 'dompurify';

import { useResizeObserver } from 'usehooks-ts';

import { Input, Table, Select, DatePicker, notification, Modal, Tooltip, Button } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import { exception } from '@extensions/notification';

import { delayAction, getDateTimeLocal, getEnumList } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { IEmailFilter } from '@entities/email-filter';
import { IEmail } from '@entities/email';

import { EmailStatus, enumLabel as emailStatusLabel } from '@enums/email-status';
import { IEnumItem } from '@enums/enum-item';

import './emails.css';

dayjs.extend(utc);

const filterContext: string = 'Emails';

const Emails = (props: IBaseComponentProps) => {
    const { serverFetch } = props;

    const initFilter: IEmailFilter = { createdFrom: dayjs().add(-2, 'day').utc(true).startOf('day') };

    const { RangePicker } = DatePicker;

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const [api, contextHolder] = notification.useNotification();

    const d = useAppDispatch();

    const filter = useAppSelector<IEmailFilter>((s) => s.filters[filterContext]);

    const [entities, setEntities] = useState<Array<IEmail>>([]);
    const [statuses] = useState<Array<IEnumItem>>(getEnumList(EmailStatus, emailStatusLabel));
    const [loading, setLoading] = useState<boolean>(false);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);

    const [openModal, setOpenModal] = useState<boolean>();
    const [currentEmail, setCurrentEmail] = useState<IEmail | undefined>();

    const [showFilter, setShowFilter] = useState<boolean>(true);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);

        setEntities([]);
        setLoading(true);

        serverFetch('notifications/emails', { method: 'GET', queryParams: filter })
            .then((data: any) => {
                setEntities(data);
                setLoading(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения email', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }
    }, [filter]);

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => setRefreshRequired(true),
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            key='searchText'
                            placeholder='Email/ Получатель / ИНН / Контрагент / Тема /Сообщение'
                            value={filter?.searchText}
                            style={{ width: 320 }}
                            onChange={(data) => {
                                d(setFilter({ ...filter, searchText: data.target.value }, filterContext));
                            }}
                        />,
                        <RangePicker
                            style={{ width: 260 }}
                            allowEmpty={[true, true]}
                            key='created'
                            format='DD.MM.YYYY'
                            placeholder={['Создано с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[
                                filter?.createdFrom ? dayjs(filter?.createdFrom) : null,
                                filter?.createdTo ? dayjs(filter?.createdTo) : null,
                            ]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                createdFrom: undefined,
                                                createdTo: undefined,
                                            },
                                            filterContext,
                                        ),
                                    );
                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            createdFrom: value[0]
                                                ? dayjs(value[0]).utc(true).startOf('day').toString()
                                                : undefined,
                                            createdTo: value[1]
                                                ? dayjs(value[1]).utc(true).endOf('day').toString()
                                                : undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                            }}
                        />,
                        <Select
                            key='status'
                            placeholder='Статус'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={statuses.map((s) => {
                                return { value: s.value, label: s.label };
                            })}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<IEmail> = [
            {
                title: 'Получатель',
                dataIndex: 'recipient',
                width: 200,
            },
            {
                title: 'Email',
                dataIndex: 'to',
                width: 300,
            },
            {
                title: 'Контрагент',
                dataIndex: 'legalName',
                width: 250,
            },
            {
                title: 'ИНН',
                dataIndex: 'inn',
                width: 200,
            },
            {
                title: 'Тема',
                width: 300,
                dataIndex: 'subject',
                render: (_, record: IEmail) => {
                    const dots = record.subject && record.subject.length > 38 ? '...' : '';
                    return (
                        <Tooltip title='Открыть подробнее...'>
                            <Button
                                style={{ padding: 0, height: 'fit-content' }}
                                variant='link'
                                color='blue'
                                onClick={() => {
                                    setCurrentEmail(record);
                                    setOpenModal(true);
                                }}
                            >
                                {record.subject?.substring(0, 38)}
                                {dots}
                            </Button>
                        </Tooltip>
                    );
                },
            },
            {
                title: 'Создано',
                width: 130,
                render: (_, record) => {
                    return getDateTimeLocal(record.createdOn);
                },
            },
            {
                title: 'Статус',
                width: 140,
                render: (_, record) => {
                    return emailStatusLabel(record.status);
                },
            },
            {
                title: 'Отправлено',
                width: 130,
                render: (_, record) => {
                    return getDateTimeLocal(record.sentOn);
                },
            },
            {
                title: 'Отправитель',
                dataIndex: 'createdByName',
                width: 250,
            },

            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={entities}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                pagination={false}
                virtual={true}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58) }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 195px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            <Modal
                title={`Сообщение для ${currentEmail?.recipient} от ${getDateTimeLocal(currentEmail?.createdOn)}`}
                width={900}
                open={openModal && !!currentEmail}
                okButtonProps={{
                    style: {
                        display: 'none',
                    },
                }}
                cancelText='Закрыть'
                onCancel={() => {
                    setCurrentEmail(undefined);
                    setOpenModal(false);
                }}
                closable={false}
            >
                <div
                    className='email-modal'
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(currentEmail?.body || '', { USE_PROFILES: { html: true } }),
                    }}
                />
            </Modal>

            {contextHolder}
        </div>
    );
};

export default Emails;
